import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Typography,
  Box,
  FormControl,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  FormLabel,
  Chip,
  MenuItem,
  Select,
  InputLabel,
  Divider,
} from "@material-ui/core";
import AddIcon from "@material-ui/icons/Add";
import ImportExportIcon from "@material-ui/icons/ImportExport";
import CloseIcon from "@material-ui/icons/Close";
import PersonAddIcon from "@material-ui/icons/PersonAdd";
import { MuiPickersUtilsProvider, KeyboardDatePicker } from "@material-ui/pickers";
import DateFnsUtils from "@date-io/date-fns";
import { apiPath } from "api";
import { generalAuthTokenHeader } from "__helpers/auth-header";
import { userService } from "_services/user.service";
import { getActiveFacilityId } from "__helpers/activeFacility";
import { format } from 'date-fns';
import { Alert, AlertTitle } from "@material-ui/lab";
import maxAllyIcon from "assets/img/icons/maxally-icon.svg";

/**
 * Contacts in alphabetical order by name.
 *
 * Applied at render rather than on fetch because the list has two sources —
 * the dialog's own fetch and the `contactList` prop it falls back to before
 * that resolves — and sorting in one place keeps them consistent.
 *
 * A stable order also stops a row jumping after an invite, which is what an
 * earlier attempt to preserve the previous order was reaching for (it
 * computed an order and then set the unsorted data anyway).
 *
 * `sensitivity: "base"` so "bharat" and "BHARAT" sort together rather than
 * every lowercase name landing after every uppercase one.
 */
function sortByName(contacts) {
  if (!Array.isArray(contacts)) return contacts;
  return [...contacts].sort((a, b) =>
    (a.contactName || "").localeCompare(b.contactName || "", undefined, {
      sensitivity: "base"
    })
  );
}

class ContactDialog extends React.Component {
  constructor(props) {
    super(props);

    const patientId = JSON.parse(localStorage.getItem("_selected_patient_list"));
    console.log("_selected_patient_list", patientId);
    const userinfo = JSON.parse(localStorage.getItem("_user_info")) || {};

    this.state = {
      openContactDialog: false,
      openNewContactDialog: false,
      openEditContactDialog: false,
      editContact: null,
      contactName: "",
      email: "",
      mobilePhone: "",
      homePhone: "",
      relationship: "",
      patientId: patientId,
      dischargePlannerId:
        (userinfo.dischargePlanner && userinfo.dischargePlanner.id) ||
        (userinfo && userinfo.createdById) ||
        null,
      facilityId: getActiveFacilityId(),
      isPrimary: false,
      contactList: [],
      selectedType: '', // Initialize selectedType in state
      open: false,
      onClose: false,
      inviteLoading: false,
      confirmAction: null,
      openConfirmDialog: false,

      // "Family authorized this number/email" attestation. Gates the Invite button on
      // BOTH invite paths (the apply-default-template popup and the plain confirmation
      // dialog). Always reset to false when either dialog opens, so a DP who authorized
      // one contact never gets a pre-ticked box for the next one.
      inviteAuthorized: false,

      // "Apply default template" popup — shown on first-time invite per contact
      // when the facility has a configured default. Drives:
      //   1. an existing-flow invite (POST /patient-consumer/create), then
      //   2. if checkbox kept, a bulk-patient-task POST with contactId + isPccContact
      //      so the cloned tasks' surveys route to the just-invited contact.
      defaultTemplate: null,                  // { id, templateName } or null
      defaultTemplateFetched: false,          // dedup the GET across multiple opens
      applyDefaultTemplateDialogOpen: false,
      pendingInviteContact: null,
      applyTemplateChecked: true,
      popupEstimatedDischargeDate: null,
      popupDateError: "",
    };
  }
  componentDidMount() {
    console.log("ContactDialog mounted. Open status:", this.props.open);
  }
  handleTypeChange = (e) => {
    this.setState({ selectedType: e.target.value }); // Update state on change
  };

  handleOpenContactDialog = async () => {
    try {
      await this.fetchContactList();
    } catch (err) {
      console.error("Error in fetchContactList:", err);
      // Optionally show a notification or fallback here
    }
    this.setState({ openContactDialog: true });
  };


  handleCloseContactDialog = () => {
    this.setState({ openContactDialog: false });
  };

  handleOpenNewContactDialog = () => {
    this.setState({
      openNewContactDialog: true,
      contactName: '',
      mobilePhone: '',
      homePhone: '',
      email: '',

      relationship: '',

    });
  };


  handleCloseNewContactDialog = () => {
    this.setState({ openNewContactDialog: false });
  };

  fetchImportContact = async () => {
    let showNotification = {};
    const apiUrl = apiPath.GetImportPccPatientContacts + "?patientId=" + this.props.patientId + "&dpId=" + this.state.dischargePlannerId;

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: generalAuthTokenHeader(),
      });

      if (response.status === 401) {
        userService.logout();
        window.location.reload(true);
        return;
      }

      if (response.status === 400) {
        showNotification = { title: "Import", message: "Bad Request", type: "danger" };
      } else {
        const data = await response.json();
        const messageText = data.message || "Import successful";

        if (messageText === "No contact data found.") {
          showNotification = { title: "Import", message: messageText, type: "warning" };
        } else {
          showNotification = { title: "Import", message: messageText, type: "success" };
          await this.fetchContactList();
        }
      }

      if (showNotification.title) userService.showNotification(showNotification);
    } catch (error) {
      console.error("Error importing contacts", error);
      userService.showNotification({
        title: "Import",
        message: "Server Error",
        type: "danger",
      });
    }
  };

  handleOpenEditContactDialog = async (contact) => {
    console.log(contact.patientContactId)
    const payload = {
      patientContactId: contact.patientContactId,
      dpId: this.state.dischargePlannerId,
      isPccPatientContact: contact.isPccPatientContact,
    };
    const apiUrl = apiPath.GetPatientContactById;

    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          ...generalAuthTokenHeader(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        const data = await response.json();

        this.setState({
          openEditContactDialog: true,
          editContact: data,
          contactName: data.contactName,
          email: data.email || "",
          mobilePhone: data.mobilePhone,
          homePhone: data.homePhone,
          relationship: data.relationship,
          isPrimary: data.isPrimary,
          isPccPatientContact: data.isPccPatientContact,
        });
      } else {
        const errorData = await response.json();
        console.error("Failed to fetch contact by ID:", errorData);
        userService.showNotification({
          title: "Contact",
          message: "Failed to fetch contact details.",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error fetching contact by ID:", error);
      userService.showNotification({
        title: "Contact",
        message: "Error while fetching contact details.",
        type: "danger",
      });
    }
  };


  formatDateToServerFormat = (date) => {
    return date ? format(new Date(date), 'yyyy-MM-dd') : null;
  };

  handleCloseEditContactDialog = () => {
    this.setState({ openEditContactDialog: false, editContact: null });
  };



  handleAddContact = async () => {
    const {
      contactName,
      mobilePhone,
      homePhone,
      email,
      isPrimary,
      relationship,
    } = this.state;

    // Email validation regex
    const isValidEmail = email === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    // Validate required fields
    if (!contactName || !relationship) {
      userService.showNotification({
        title: "",
        message: "Please fill all required fields.",
        type: "danger",
      });
      return; // Stop if validation fails
    }

    if (!email && !mobilePhone) {
      userService.showNotification({
        title: "",
        message: "Please fill at least Email or Mobile Phone.",
        type: "danger",
      });
      return;
    }
    // if (isPrimary && this.state.contactList.some(c => c.isPrimary)) {
    //   userService.showNotification({
    //     title: "Primary Contact",
    //     message: "A primary contact already exists. Only one primary contact is allowed.",
    //     type: "warning",
    //   });
    //   return;
    // }
    if (!isValidEmail) {
      userService.showNotification({
        title: "",
        message: "Please enter a valid email address.",
        type: "danger",
      });
      return;
    }
    const patientId = JSON.parse(localStorage.getItem("_selected_patient_list")); // ← Get from localStorage

    const payload = {
      name: contactName,
      mobilePhone,
      homePhone,
      email,
      isPrimary,
      relationship,
      dpId: this.state.dischargePlannerId,
      patientId: this.props.patientId,
    };

    console.log("payload", payload);

    const apiUrl = apiPath.AddPatientContactSave;
    let showNotification = {};


    try {
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          ...generalAuthTokenHeader(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (response.status === 400) {
        showNotification = { title: "Survey", message: "Bad Request", type: "danger" };
      } else if (response.status === 401) {
        userService.logout();
        window.location.reload(true);
      } else if (!response.ok) {
        showNotification = { title: "Survey", message: "Error submitting contact", type: "danger" };
      } else {
        showNotification = {
          title: "Contact",
          message: "Contact added successfully!",
          type: "success",
        };

        this.setState({ openNewContactDialog: false });
        if (this.props.onUpdate) {
          console.log("Calling parent onUpdate to reload contact list...");
          this.props.onUpdate();
          console.log("Parent onUpdate finished.");
        }

        // Refresh contact list if needed

      }

      if (showNotification.title) userService.showNotification(showNotification);
    } catch (error) {
      console.error("Error submitting contact", error);
      userService.showNotification({ title: "Contact", message: "Server Error", type: "danger" });
    }


  };

  handleUpdateContact = async () => {
    const {
      contactName,
      mobilePhone,
      homePhone,
      email,
      isPrimary,
      relationship,
      editContact, // This contains patientContactId
    } = this.state;
    // Email validation regex
    const isValidEmail = email === "" || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    // Validate required fields
    if (!contactName || !relationship) {
      userService.showNotification({
        title: "",
        message: "Please fill all required fields.",
        type: "danger",
      });
      return; // Stop if validation fails
    }

    if (!email && !mobilePhone) {
      userService.showNotification({
        title: "",
        message: "Please fill at least Email or Mobile Phone.",
        type: "danger",
      });
      return;
    }

    // if (
    //   isPrimary &&
    //   this.state.contactList.some(
    //     (c) => c.isPrimary && c.patientContactId !== this.state.editContact.patientContactId
    //   )
    // ) {
    //   userService.showNotification({
    //     title: "Primary Contact",
    //     message: "A primary contact already exists. Only one primary contact is allowed.",
    //     type: "warning",
    //   });
    //   return;
    // }


    if (!isValidEmail) {
      userService.showNotification({
        title: "",
        message: "Please enter a valid email address.",
        type: "danger",
      });
      return;
    }

    const patientId = JSON.parse(localStorage.getItem("_selected_patient_list")); // ← Get from localStorage

    const payload = {
      contactName,
      mobilePhone,
      homePhone,
      email,

      relationship,
      dpId: this.state.dischargePlannerId,
      patientId: this.props.patientId,
      isPccPatientContact: this.state.isPccPatientContact,
      isPrimary: this.state.isPrimary
    };

    console.log("payload", payload);
    const apiUrl = `${apiPath.UpdatePatientContact}/${editContact.patientContactId}`;

    try {
      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          ...generalAuthTokenHeader(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        userService.showNotification({
          title: "Contact",
          message: "Contact updated successfully.",
          type: "success",
        });

        // Optionally refresh the contact list or close modal here
        if (this.props.onUpdate) {
          console.log("Calling parent onUpdate to reload contact list...");
          this.props.onUpdate();
          console.log("Parent onUpdate finished.");
        }

        this.setState({ openEditContactDialog: false, editContact: null });
      } else {
        console.error("Update failed:", result);
        userService.showNotification({
          title: "Contact",
          message: result.message || "Failed to update contact.",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error updating contact:", error);
      userService.showNotification({
        title: "Contact",
        message: "Error while updating contact.",
        type: "danger",
      });
    }
  };

  async fetchContactList() {
    let showNotification = {};
    const patientId = JSON.parse(localStorage.getItem("_selected_patient_list"));
    console.log("patientId in  fetchContactList", patientId);

    const apiUrl = apiPath.GetAllPatientContactList + "?patientId=" + this.props.patientId + "&dpId=" + this.state.dischargePlannerId;

    try {
      const response = await fetch(apiUrl, {
        method: "GET",
        headers: generalAuthTokenHeader(),
      });

      if (response.status === 400) {
        showNotification = { title: "contact List", message: "Bad Request", type: "danger" };
      } else if (response.status === 401) {
        userService.logout();
        window.location.reload(true);
      } else if (!response.ok) {
        showNotification = { title: "contact List", message: "Error fetching data", type: "danger" };
      }

      const data = await response.json();
      console.log("contactList Fetched: ", data);

      // Ordering is applied once in render(), where this list and the prop
      // fallback converge — see sortByName there.
      this.setState({ contactList: data });

      if (showNotification.title) userService.showNotification(showNotification);
    } catch (error) {
      console.error("Error fetching input types", error);
      showNotification = { title: "contact List", message: "Server Error", type: "danger" };
      userService.showNotification(showNotification);
    }
  }

  /**
   * Contact value shown in the invite confirmation, in full (unmasked, per client) so the
   * DP can actually check it against what the family told them. Mobile phone wins over
   * email because that is the only phone the invite ever reaches — the backend resolves
   * the contact to cellPhone (PCC) / mobilePhone (SF) and never uses homePhone.
   */
  getInviteValue = (contact) => {
    const phone = String((contact && contact.mobilePhone) || "").trim();
    if (phone) {
      return phone;
    }
    return String((contact && contact.email) || "").trim() || "—";
  };

  /**
   * Shared "confirm the number on file" block. Rendered on both invite paths so the
   * copy lives in exactly one place. "number/email" is deliberately fixed wording —
   * it does not switch to match whichever value is shown.
   */
  renderInviteAuthorization = (contact) => (
    <Box>
      <Typography variant="body2">
        On file for <b>{(contact && contact.contactName) || "this contact"}</b>:{" "}
        {this.getInviteValue(contact)}
      </Typography>
      <Typography variant="body2" style={{ marginTop: 4 }}>
        Confirm this is still the best number/email.
      </Typography>
      <FormControlLabel
        style={{ marginTop: 8 }}
        control={
          <Checkbox
            checked={this.state.inviteAuthorized}
            onChange={(e) => this.setState({ inviteAuthorized: e.target.checked })}
            color="primary"
          />
        }
        label="Family authorized this number/email"
      />
    </Box>
  );

  openConfirmationDialog = async (type, contact) => {
    // First-time-invite branch: try to show the "Apply default template"
    // popup if the facility has a configured default. Reactivate/deactivate
    // and re-invites (where this branch isn't reached because the UI doesn't
    // render the Invite button) bypass this and go straight to the legacy
    // confirm dialog.
    if (type === "invite") {
      const tpl = await this.fetchDefaultTemplateIfNeeded();
      // Enforce one-contact-per-patient for the default template: only offer the
      // "Apply default template" popup if the facility has a default AND it has
      // not already been applied to a contact for this patient.
      if (tpl && !(await this.isDefaultTemplateAppliedForPatient(tpl.id))) {
        const prefill = this.props.estimatedDischargeDate || null;
        this.setState({
          defaultTemplate: tpl,
          pendingInviteContact: contact,
          applyTemplateChecked: true,
          popupEstimatedDischargeDate: prefill,
          popupDateError: "",
          applyDefaultTemplateDialogOpen: true,
          inviteAuthorized: false,
        });
        return;
      }

      // No default template, or it was already applied to another contact for this
      // patient — confirm the number on file before sending. (The template path carries
      // the same attestation inside its own popup, so both routes are gated.)
      this.setState({
        confirmAction: { type, contact },
        openConfirmDialog: true,
        inviteAuthorized: false,
      });
      return;
    }

    // Deactivate / Reactivate still require explicit confirmation.
    this.setState({
      confirmAction: { type, contact },
      openConfirmDialog: true,
    });
  };

  /**
   * Lazy-fetch the facility's default template once per dialog lifetime.
   * Returns the template ({id, templateName}) or null when the facility has
   * no default configured (404 from /patient-task-template/default).
   */
  fetchDefaultTemplateIfNeeded = async () => {
    if (this.state.defaultTemplateFetched) {
      return this.state.defaultTemplate;
    }
    if (!this.state.facilityId) {
      this.setState({ defaultTemplateFetched: true, defaultTemplate: null });
      return null;
    }
    try {
      const url = `${apiPath.getDefaultTaskTemplateByFacility}?facilityId=${this.state.facilityId}`;
      const response = await fetch(url, { headers: generalAuthTokenHeader() });
      if (response.status === 401) {
        userService.logout();
        window.location.reload(true);
        return null;
      }
      if (response.status === 404) {
        this.setState({ defaultTemplateFetched: true, defaultTemplate: null });
        return null;
      }
      if (!response.ok) {
        // Unknown error — degrade gracefully to legacy invite flow rather than blocking.
        console.warn("getDefaultTaskTemplateByFacility returned", response.status);
        this.setState({ defaultTemplateFetched: true, defaultTemplate: null });
        return null;
      }
      const data = await response.json();
      this.setState({ defaultTemplateFetched: true, defaultTemplate: data });
      return data;
    } catch (err) {
      console.error("Error fetching default template:", err);
      this.setState({ defaultTemplateFetched: true, defaultTemplate: null });
      return null;
    }
  };

  /**
   * Returns true when the facility's default template has already been applied
   * to (any contact of) this patient — used to enforce one-contact-per-patient
   * for the default template. Fails open (returns false → popup still shown) on
   * any error, matching fetchDefaultTemplateIfNeeded's graceful degradation.
   */
  isDefaultTemplateAppliedForPatient = async (templateId) => {
    const patientId = this.props.patientId || this.state.patientId;
    if (!patientId || !templateId) {
      return false;
    }
    try {
      const url = `${apiPath.checkTemplateApplied}?patientId=${patientId}&templateId=${templateId}`;
      const response = await fetch(url, { headers: generalAuthTokenHeader() });
      if (response.status === 401) {
        userService.logout();
        window.location.reload(true);
        return false;
      }
      if (!response.ok) {
        console.warn("checkTemplateApplied returned", response.status);
        return false;
      }
      const data = await response.json();
      return !!(data && data.applied);
    } catch (err) {
      console.error("Error checking if default template already applied:", err);
      return false;
    }
  };

  handleApplyDefaultTemplateCancel = () => {
    this.setState({
      applyDefaultTemplateDialogOpen: false,
      pendingInviteContact: null,
      popupDateError: "",
      inviteAuthorized: false,
    });
  };

  // Invite button on the "Apply default template" dialog. Validates input,
  // then closes the template dialog and runs the invite directly. The extra
  // Confirmation dialog step was removed per client request (kept commented
  // below for future re-enable). The actual work runs in
  // runApplyDefaultTemplateInvite.
  handleApplyDefaultTemplateConfirm = async () => {
    const {
      pendingInviteContact,
      applyTemplateChecked,
      popupEstimatedDischargeDate,
      inviteAuthorized,
    } = this.state;

    if (!pendingInviteContact) {
      this.setState({ applyDefaultTemplateDialogOpen: false });
      return;
    }

    // The Invite button is disabled until the family-authorization box is ticked; this
    // guard just makes the rule hold for any non-UI caller too.
    if (!inviteAuthorized) {
      return;
    }

    // Validation: discharge date is mandatory only when the box is checked,
    // because the template's "send X days after discharge" surveys need a date.
    if (applyTemplateChecked && !popupEstimatedDischargeDate) {
      this.setState({ popupDateError: "Estimated discharge date is required when applying the default template." });
      return;
    }

    // Keep pendingInviteContact / applyTemplateChecked / popupEstimatedDischargeDate
    // in state — the worker (runApplyDefaultTemplateInvite) still needs them.
    this.setState({
      applyDefaultTemplateDialogOpen: false,
      popupDateError: "",
      // Confirmation step removed per client request — run the invite directly.
      // confirmAction: { type: "invite", contact: pendingInviteContact, fromTemplate: true },
      // openConfirmDialog: true,
    });
    await this.runApplyDefaultTemplateInvite();
  };

  // Performs the actual invite + default-template apply. Invoked from
  // handleConfirmAction after the user confirms the Confirmation dialog.
  runApplyDefaultTemplateInvite = async () => {
    const {
      pendingInviteContact,
      applyTemplateChecked,
      popupEstimatedDischargeDate,
    } = this.state;

    if (!pendingInviteContact) {
      return;
    }

    // Step 0: if the popup date differs from the patient's currently-stored
    // estimated discharge date, push the change up to the parent so the
    // patient row is updated. Failure here is non-blocking (matches the
    // accepted invite/template-apply partial-completion pattern): the invite
    // still proceeds and the new date is still used for THIS batch's survey
    // scheduling (passed explicitly in the bulk-patient-task payload), but
    // the patient row stays at its old date until the DP retries via the
    // Edit Patient modal.
    const newIsoDate = this._toIsoDateString(popupEstimatedDischargeDate);
    const oldIsoDate = this._toIsoDateString(this.props.estimatedDischargeDate);
    if (newIsoDate && newIsoDate !== oldIsoDate && this.props.onUpdateEstimatedDischargeDate) {
      try {
        await this.props.onUpdateEstimatedDischargeDate(newIsoDate);
      } catch (err) {
        console.warn("Failed to update patient's estimated discharge date:", err);
        userService.showNotification({
          title: "Estimated Discharge Date",
          message: "Could not update the patient's discharge date. Invite will continue; please retry via Edit Patient.",
          type: "warning",
        });
      }
    }

    // Step 1: existing invite call (unchanged contract).
    await this.handleInviteAsConsumer(pendingInviteContact);

    // Step 2: if user kept the checkbox, fire the template apply with the
    // invited contact id (so its surveys route to that specific contact).
    if (applyTemplateChecked) {
      await this.applyDefaultTemplateForContact(
        pendingInviteContact,
        popupEstimatedDischargeDate,
      );
    }

    this.setState({ pendingInviteContact: null });
  };

  applyDefaultTemplateForContact = async (contact, estimatedDischargeDate) => {
    const { defaultTemplate, dischargePlannerId, facilityId } = this.state;
    // Match the existing invite flow: patient id comes from the parent via
    // props (this.props.patientId), not from this.state.patientId which is
    // hydrated from localStorage._selected_patient_list and can be null
    // during the consumer-invite path.
    const patientId = this.props.patientId || this.state.patientId;
    if (!defaultTemplate || !defaultTemplate.id) {
      return;
    }
    const payload = [{
      patientId,
      templateId: defaultTemplate.id,
      templateName: defaultTemplate.templateName,
      dischargePlannerId,
      facilityId,
      patientContactId: contact.patientContactId,
      isPccPatientContact: !!contact.isPccPatientContact,
      // Backend accepts ISO yyyy-MM-dd; the popup state holds either that or
      // a Date — normalize here. format() from date-fns handles Date inputs.
      estimatedDischargeDate: this._toIsoDateString(estimatedDischargeDate),
    }];

    try {
      const response = await fetch(apiPath.PatientTaskBulk, {
        method: "POST",
        headers: generalAuthTokenHeader(),
        body: JSON.stringify(payload),
      });
      if (response.status === 401) {
        userService.logout();
        window.location.reload(true);
        return;
      }
      if (!response.ok && response.status !== 201) {
        userService.showNotification({
          title: "Default Template",
          message: "Invite succeeded but applying the default template failed.",
          type: "danger",
        });
        return;
      }
      userService.showNotification({
        title: "Default Template",
        message: `Default template applied for ${contact.contactName}.`,
        type: "success",
      });
      if (this.props.onUpdate) {
        this.props.onUpdate();
      }
    } catch (err) {
      console.error("Error applying default template:", err);
      userService.showNotification({
        title: "Default Template",
        message: "Server error while applying the default template.",
        type: "danger",
      });
    }
  };

  _toIsoDateString = (d) => {
    if (!d) return null;
    if (d instanceof Date) {
      try {
        return format(d, "yyyy-MM-dd");
      } catch (e) {
        return null;
      }
    }
    if (typeof d === "string") {
      const s = d.trim();
      if (!s) return null;
      // Already ISO (yyyy-MM-dd, optionally with a time part) — take the date part.
      if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.substring(0, 10);
      // US display format MM/dd/yyyy — the backend LocalDate only accepts ISO, so
      // convert it explicitly. (Previously this branch blindly substring'd the first
      // 10 chars, passing "07/07/2026" through unchanged and 400ing the backend.)
      const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
      if (mdy) {
        const [, mm, dd, yyyy] = mdy;
        return `${yyyy}-${mm.padStart(2, "0")}-${dd.padStart(2, "0")}`;
      }
      // Last resort: let the engine try to parse it, then normalize to ISO.
      const parsed = new Date(s);
      if (!isNaN(parsed)) return format(parsed, "yyyy-MM-dd");
      return null;
    }
    return null;
  };

  handleConfirmAction = async () => {
    const { confirmAction, inviteAuthorized } = this.state;
    if (!confirmAction) return;

    // Invites require the family-authorization attestation; deactivate/reactivate do not.
    if (confirmAction.type === "invite" && !inviteAuthorized) {
      return;
    }

    this.setState({ openConfirmDialog: false });

    if (confirmAction.type === "invite") {
      if (confirmAction.fromTemplate) {
        await this.runApplyDefaultTemplateInvite();
      } else {
        await this.handleInviteAsConsumer(confirmAction.contact);
      }
    } else if (confirmAction.type === "deactivate") {
      await this.handleDeactivateConsumer(confirmAction.contact);
    } else if (confirmAction.type === "reactivate") {
      await this.handleReactivateConsumer(confirmAction.contact);
    }

    this.setState({ confirmAction: null, inviteAuthorized: false });
  };

  handleInviteAsConsumer = async (contact) => {
    this.setState({ inviteLoading: true });

    const payload = {
      patientId: this.props.patientId,
      contactId: contact.patientContactId,
      isPccContact: contact.isPccPatientContact || false,
      invitedBy: this.state.dischargePlannerId,
    };

    try {
      const response = await fetch(apiPath.CreatePatientConsumer, {
        method: "POST",
        headers: generalAuthTokenHeader(),
        body: JSON.stringify(payload),
      });

      if (response.status === 201 || response.ok) {
        userService.showNotification({
          title: "Patient Consumer",
          message: `${contact.contactName} has been invited as a patient consumer.`,
          type: "success",
        });
        if (this.props.onUpdate) {
          this.props.onUpdate();
        }
      } else if (response.status === 401) {
        userService.logout();
        window.location.reload(true);
      } else {
        let backendMessage = "";
        try {
          const data = await response.json();
          if (data && data.message) backendMessage = data.message;
        } catch (parseErr) {
          backendMessage = "";
        }
        userService.showNotification({
          title: "Patient Consumer",
          message: backendMessage || "Failed to invite contact as patient consumer.",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error inviting patient consumer:", error);
      userService.showNotification({
        title: "Patient Consumer",
        message: "Server error while inviting patient consumer.",
        type: "danger",
      });
    }

    this.setState({ inviteLoading: false });
  };

  handleDeactivateConsumer = async (contact) => {
    const apiUrl = `${apiPath.DeactivatePatientConsumer}/${contact.patientConsumerId}/deactivate?deactivatedBy=${this.state.dischargePlannerId}`;

    try {
      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: generalAuthTokenHeader(),
      });

      if (response.ok) {
        userService.showNotification({
          title: "Patient Consumer",
          message: `Consumer access deactivated for ${contact.contactName}.`,
          type: "success",
        });
        if (this.props.onUpdate) {
          this.props.onUpdate();
        }
      } else if (response.status === 401) {
        userService.logout();
        window.location.reload(true);
      } else {
        userService.showNotification({
          title: "Patient Consumer",
          message: "Failed to deactivate patient consumer.",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error deactivating patient consumer:", error);
      userService.showNotification({
        title: "Patient Consumer",
        message: "Server error while deactivating patient consumer.",
        type: "danger",
      });
    }
  };

  handleReactivateConsumer = async (contact) => {
    const apiUrl = `${apiPath.ReactivatePatientConsumer}/${contact.patientConsumerId}/reactivate`;

    try {
      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: generalAuthTokenHeader(),
      });

      if (response.ok) {
        userService.showNotification({
          title: "Patient Consumer",
          message: `Consumer access reactivated for ${contact.contactName}.`,
          type: "success",
        });
        if (this.props.onUpdate) {
          this.props.onUpdate();
        }
      } else if (response.status === 401) {
        userService.logout();
        window.location.reload(true);
      } else {
        userService.showNotification({
          title: "Patient Consumer",
          message: "Failed to reactivate patient consumer.",
          type: "danger",
        });
      }
    } catch (error) {
      console.error("Error reactivating patient consumer:", error);
      userService.showNotification({
        title: "Patient Consumer",
        message: "Server error while reactivating patient consumer.",
        type: "danger",
      });
    }
  };

  render() {
   const { open, onClose } = this.props;
    const contactList = sortByName(
      this.state.contactList.length > 0
        ? this.state.contactList
        : this.props.contactList
    );
    return (
      <div style={{ marginLeft: 10}}>
        {/* <Box display="flex" marginRight={1}>
          <Button className="pointer btn1 borderradius2" onClick={this.handleOpenContactDialog} style={{ borderRadius: "0px 20px 20px 0px !important" }}>
            Contact
          </Button>
        </Box> */}
        {/* Main Contact Dialog */}
         <Dialog open={open}  onClose={onClose} fullWidth maxWidth="md">
          <DialogTitle>
          Invite to MaxAlly
            <Box component="p" style={{ fontSize: "15px", color: "#555", margin: "4px 0 0 0", fontWeight: "normal" }}>
              Gives {this.props.patientName || "this patient"}'s family every discharge document and care detail in one place - they ask MaxAlly directly instead of calling you. AI Risk radar checks in automatically after discharge and flags readmission risk early.
            </Box>
            <Box display="flex" justifyContent="flex-end" position="absolute" right={16} top={8}>
              <Button
                startIcon={<ImportExportIcon />}
                size="small"
                style={{ marginRight: 8 }}
                onClick={this.fetchImportContact}
              >
                Import Contact
              </Button>
              <Button
                startIcon={<AddIcon />}
                color="primary"
                variant="contained"
                size="small"
                onClick={this.handleOpenNewContactDialog}
              >
                New Contact
              </Button>
            </Box>
          </DialogTitle>
          <DialogContent>
            {contactList?.length === 0 ? (
              <Typography variant="body1" align="center" color="textSecondary">
                No contacts found.
              </Typography>
            ) : (
              contactList?.map((contact) => (
                <Box key={contact.pccContactListId || contact.patientContactId} borderBottom={1} mb={2} pb={1}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Box flex={1}>
                      <Box display="flex" alignItems="center">
                        <Typography variant="subtitle1">
                          {contact.contactName} - {contact.relationship}
                        </Typography>
                        {contact.isPrimary && (
                          <Chip
                            size="small"
                            label="Primary"
                            color="primary"
                            style={{ marginLeft: 8 }}
                          />
                        )}
                        {contact.isPatientConsumer && (
                          <Chip
                            size="small"
                            label={contact.consumerStatus}
                            style={{
                              marginLeft: 8,
                              backgroundColor: contact.consumerStatus === "ACTIVE" ? "#4caf50"
                                : contact.consumerStatus === "INVITED" ? "#ff9800"
                                : "#9e9e9e",
                              color: "#fff",
                            }}
                          />
                        )}
                        {contact.isPccPatientContact && (
                          <Chip
                            size="small"
                            label="Imported"
                            style={{
                              marginLeft: 8,
                              backgroundColor: "#1976d2",
                              color: "#fff",
                            }}
                          />
                        )}
                        {contact.removedFromPcc && (
                          <Chip
                            size="small"
                            label="Removed from PCC"
                            style={{
                              marginLeft: 8,
                              backgroundColor: "#d32f2f",
                              color: "#fff",
                            }}
                          />
                        )}
                      </Box>

                      {/* Contact details */}
                      <Box mt={1}>
                        {[
                          { label: "Mobile:", value: contact.mobilePhone || "N/A" },
                          { label: "Home:", value: contact.homePhone || "N/A" },
                          { label: "Email:", value: contact.email || "N/A" },
                          {
                            label: "Type:", value: contact.contactType?.length > 0
                              ? contact.contactType.map((t) => t.contactType).join(", ")
                              : "N/A"
                          },
                        ].map((item, idx) => (
                          <Box key={idx} display="flex" alignItems="center" mb={0.5}>
                            <Typography
                              variant="body2"
                              style={{ width: 60, fontWeight: 500 }} // fixed width for alignment
                            >
                              {item.label}
                            </Typography>
                            <Typography variant="body2">{item.value}</Typography>
                          </Box>
                        ))}
                      </Box>


                    </Box>

                    <Box display="flex" alignItems="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => this.handleOpenEditContactDialog({
                          ...contact,
                          patientContactId: contact.patientContactId
                        })}
                        style={{ marginLeft: 16 }}
                      >
                        Edit
                      </Button>
                       {/* Temporarily commented out — consumer invite flow disabled */}
                      {!contact.isPatientConsumer && (contact.email || contact.mobilePhone) && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          startIcon={<PersonAddIcon />}
                          onClick={() => this.openConfirmationDialog("invite", contact)}
                          style={{ marginLeft: 8 }}
                          disabled={this.state.inviteLoading}
                        >
                          Invite
                        </Button>
                      )}

                      {contact.isPatientConsumer && contact.consumerStatus === "ACTIVE" && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="secondary"
                          onClick={() => this.openConfirmationDialog("deactivate", contact)}
                          style={{ marginLeft: 8 }}
                        >
                          Deactivate
                        </Button>
                      )}

                      {contact.isPatientConsumer && contact.consumerStatus === "DEACTIVATED" && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => this.openConfirmationDialog("reactivate", contact)}
                          style={{ marginLeft: 8 }}
                        >
                          Reactivate
                        </Button>
                      )} 
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={onClose} color="secondary">
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* New Contact Dialog */}
        <Dialog open={this.state.openNewContactDialog} onClose={this.handleCloseNewContactDialog} fullWidth maxWidth="sm">
          <DialogTitle>
            Add New Contact
            <IconButton
              aria-label="close"
              onClick={this.handleCloseNewContactDialog}
              style={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            <TextField
              margin="dense"
              label="Name"
              name="contactName"
              InputLabelProps={{ className: "required-label" }}
              fullWidth
              value={this.state.contactName}
              onChange={(e) => this.setState({ contactName: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Email"
              name="email"
              type="email"
              InputLabelProps={{ className: "required-label" }}
              fullWidth
              value={this.state.email}
              error={this.state.email !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.state.email)}
              helperText={
                this.state.email !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.state.email)
                  ? "Invalid email format"
                  : ""
              }
              onChange={(e) => this.setState({ email: e.target.value })}
            />
            {/* OR label between fields */}
            <div
              style={{
                textAlign: "left",
                marginTop: "5px",   // smaller space above OR
                marginBottom: "0px", // no extra space below OR
                marginLeft: "10px",
                fontWeight: "bold",
                fontSize: "10px"
              }}
            >
              OR
            </div>


            <TextField
              margin="dense"
              label="Mobile Phone"
              name="mobilePhone"
              InputLabelProps={{ className: "required-label" }}
              fullWidth
              value={this.state.mobilePhone}
              onChange={(e) => {
                const input = e.target.value;
                // Allow digits, space, parentheses, hyphens only
                const formatted = input.replace(/[^\d()\-\s]/g, "");
                this.setState({ mobilePhone: formatted });
              }}

              style={{
                marginTop:
                  this.state.mobilePhone !== "" ||
                    document.activeElement.name === "mobilePhone"
                    ? "8px" // when focused or filled
                    : "0.3px"  // default reduced margin
              }}

            />
            <TextField
              margin="dense"
              label="Home Phone"
              name="homePhone"
              fullWidth
              value={this.state.homePhone}
              onChange={(e) => {
                const input = e.target.value;
                // Allow digits, space, parentheses, hyphens only
                const formatted = input.replace(/[^\d()\-\s]/g, "");
                this.setState({ homePhone: formatted });
              }}
            />
            <TextField
              margin="dense"
              label="Relationship"
              name="relationship"
              InputLabelProps={{ className: "required-label" }}
              fullWidth
              value={this.state.relationship}
              onChange={(e) => this.setState({ relationship: e.target.value })}
            />
            <FormControl component="fieldset" style={{ marginTop: 12 }}>
              <FormLabel component="legend">Set as Primary</FormLabel>
              <RadioGroup
                row
                value={this.state.isPrimary ? "yes" : "no"}
                onChange={(e) => this.setState({ isPrimary: e.target.value === "yes" })}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>

            {/* <Box mt={2}>
              <FormControl fullWidth>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    format="MM/dd/yyyy"
                    margin="normal"
                    label=""
                    value={this.state.}
                    onChange={this.handleDate}
                    KeyboardButtonProps={{ "aria-label": "change date" }}
                  />
                </MuiPickersUtilsProvider>
              </FormControl>
            </Box> */}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCloseNewContactDialog} color="secondary">
              Cancel
            </Button>
            <Button color="primary" variant="contained" onClick={this.handleAddContact}>
              Add
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Contact Dialog */}
        <Dialog open={this.state.openEditContactDialog} onClose={this.handleCloseEditContactDialog} fullWidth maxWidth="sm">
          <DialogTitle>
            Edit Contact
            <IconButton
              aria-label="close"
              onClick={this.handleCloseEditContactDialog}
              style={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent>
            {/* Existing form fields... */}

            {this.state.isPccPatientContact && (
              <Alert severity="info" style={{ marginTop: 7 }}>
                <AlertTitle>Note</AlertTitle>
                This is only being updated in MaxMRJ. The updated information will not be pushed to PCC.
              </Alert>
            )}
          </DialogContent>
          <DialogContent>
            <TextField
              margin="dense"
              label="Name"
              name="contactName"
              InputLabelProps={{ className: "required-label" }}
              fullWidth
              value={this.state.contactName}
              onChange={(e) => this.setState({ contactName: e.target.value })}
            />
            <TextField
              margin="dense"
              label="Email"
              name="email"
              type="email"
              InputLabelProps={{ className: "required-label" }}
              fullWidth
              value={this.state.email}
              error={this.state.email !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.state.email)}
              helperText={
                this.state.email !== "" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.state.email)
                  ? "Invalid email format"
                  : ""
              }
              onChange={(e) => this.setState({ email: e.target.value })}
            />
            {/* OR label between fields */}
            <div
              style={{
                textAlign: "left",
                marginTop: "5px",   // smaller space above OR
                marginBottom: "0px", // no extra space below OR
                marginLeft: "10px",
                fontWeight: "bold",
                fontSize: "10px"
              }}
            >
              OR
            </div>


            <TextField
              margin="dense"
              label="Phone Number"
              name="mobilePhone"
              InputLabelProps={{ className: "required-label" }}
              fullWidth
              value={this.state.mobilePhone}
              onChange={(e) => {
                const input = e.target.value;
                // Allow digits, space, parentheses, hyphens only
                const formatted = input.replace(/[^\d()\-\s]/g, "");
                this.setState({ mobilePhone: formatted });
              }}

              style={{
                marginTop:
                  this.state.mobilePhone !== "" ||
                    document.activeElement.name === "mobilePhone"
                    ? "8px" // when focused or filled
                    : "0.3px"  // default reduced margin
              }}
            />
            <TextField
              margin="dense"
              label="Home Phone"
              name="homePhone"
              fullWidth
              value={this.state.homePhone}
              onChange={(e) => {
                const input = e.target.value;
                // Allow digits, space, parentheses, hyphens only
                const formatted = input.replace(/[^\d()\-\s]/g, "");
                this.setState({ homePhone: formatted });
              }}
            />
            <TextField
              margin="dense"
              label="Relationship"
              name="relationship"
              InputLabelProps={{ className: "required-label" }}
              fullWidth
              value={this.state.relationship}
              onChange={(e) => this.setState({ relationship: e.target.value })}
            />
            <FormControl component="fieldset" style={{ marginTop: 12 }}>
              <FormLabel component="legend">Set as Primary</FormLabel>
              <RadioGroup
                row
                value={this.state.isPrimary ? "yes" : "no"}
                onChange={(e) => this.setState({ isPrimary: e.target.value === "yes" })}
              >
                <FormControlLabel value="yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="no" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
            {/* <Box mt={2}>
              <FormControl fullWidth>
                <MuiPickersUtilsProvider utils={DateFnsUtils}>
                  <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    format="MM/dd/yyyy"
                    margin="normal"
                    label=""
                    value={this.state.}
                    onChange={this.handleDate}
                    KeyboardButtonProps={{ "aria-label": "change date" }}
                  />
                </MuiPickersUtilsProvider>
              </FormControl>
            </Box> */}
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleCloseEditContactDialog} color="secondary">
              Cancel
            </Button>
            <Button color="primary" variant="contained" onClick={this.handleUpdateContact}>
              Update
            </Button>
          </DialogActions>
        </Dialog>

        {/* Apply-default-template popup — first-time invite path only */}
        <Dialog
          open={this.state.applyDefaultTemplateDialogOpen}
          onClose={this.handleApplyDefaultTemplateCancel}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle>Apply default template</DialogTitle>
          <DialogContent>
            {/* Family-authorization attestation. Same block as the plain invite
                confirmation, kept above the divider so it reads as a separate decision
                from the template behaviour toggle below it. */}
            {this.renderInviteAuthorization(this.state.pendingInviteContact)}
            <Divider style={{ marginTop: 12, marginBottom: 12 }} />
            <Typography variant="body2" style={{ marginBottom: 8 }}>
              Template:&nbsp;<b>{this.state.defaultTemplate?.templateName}</b>
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={this.state.applyTemplateChecked}
                  onChange={(e) =>
                    this.setState({
                      applyTemplateChecked: e.target.checked,
                      popupDateError: "",
                    })
                  }
                  color="primary"
                />
              }
              label="Survey in this template would be sent to the invited contact"
            />
            <Box mt={1}>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <KeyboardDatePicker
                  disableToolbar
                  variant="inline"
                  format="MM/dd/yyyy"
                  margin="normal"
                  fullWidth
                  label="Estimated discharge date"
                  value={this.state.popupEstimatedDischargeDate}
                  onChange={(d) =>
                    this.setState({
                      popupEstimatedDischargeDate: d,
                      popupDateError: "",
                    })
                  }
                  KeyboardButtonProps={{ "aria-label": "estimated discharge date" }}
                  error={!!this.state.popupDateError}
                  helperText={this.state.popupDateError || ""}
                />
              </MuiPickersUtilsProvider>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={this.handleApplyDefaultTemplateCancel} color="secondary">
              Cancel
            </Button>
            <Button
              onClick={this.handleApplyDefaultTemplateConfirm}
              color="primary"
              variant="contained"
              disabled={!this.state.inviteAuthorized || this.state.inviteLoading}
            >
              Invite
            </Button>
          </DialogActions>
        </Dialog>

        {/* Confirmation Dialog for Patient Consumer Actions */}
        <Dialog
          open={this.state.openConfirmDialog}
          onClose={() => this.setState({ openConfirmDialog: false })}
          maxWidth="xs"
        >
          <DialogTitle>
            <Box display="flex" alignItems="center" gridGap={2}>
              <span>Confirm Invitation to MaxAlly app.</span>
              <img
                src={maxAllyIcon}
                alt="MaxAlly app icon"
                style={{ width: 28, height: 28 }}
              />
            </Box>
          </DialogTitle>
          <DialogContent>
            {this.state.confirmAction?.type === "invite" ? (
              this.renderInviteAuthorization(this.state.confirmAction.contact)
            ) : (
              <Typography>
                {this.state.confirmAction?.type === "deactivate" &&
                  `Deactivate consumer access for ${this.state.confirmAction.contact.contactName}? They will no longer be able to view patient information.`}
                {this.state.confirmAction?.type === "reactivate" &&
                  `Reactivate consumer access for ${this.state.confirmAction.contact.contactName}?`}
              </Typography>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() =>
                this.setState({ openConfirmDialog: false, inviteAuthorized: false })
              }
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={this.handleConfirmAction}
              color="primary"
              variant="contained"
              disabled={
                this.state.inviteLoading ||
                (this.state.confirmAction?.type === "invite" &&
                  !this.state.inviteAuthorized)
              }
            >
              {this.state.confirmAction?.type === "invite" ? "Invite" : "Confirm"}
            </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  }
}

export default ContactDialog;
